import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Reveal } from './Reveal'

export type StoryTone = 'default' | 'message' | 'accent' | 'success' | 'alert'

export type StoryLine = { text: string; tone?: 'ok' | 'info' | 'muted' }

export type StoryItem = {
  label: string
  tone?: StoryTone
  channel?: string
  author?: { name: string; initials: string; highlight?: boolean }
  title?: string
  body: ReactNode
  lines?: StoryLine[]
  typing?: boolean
  note?: ReactNode
}

const DOT: Record<StoryTone, string> = {
  default: 'bg-[var(--surface-border-strong)]',
  message: 'bg-green-400 shadow-[0_0_12px_rgba(74,222,128,0.7)]',
  accent: 'bg-[var(--color-brand-accent)] shadow-[0_0_12px_rgba(0,229,255,0.8)]',
  success: 'bg-green-400 shadow-[0_0_12px_rgba(74,222,128,0.7)]',
  alert: 'bg-[var(--marker)] shadow-[0_0_12px_rgba(252,211,77,0.7)]',
}

const CARD: Record<StoryTone, string> = {
  default: 'border-transparent bg-transparent px-0 py-0',
  message:
    'max-w-sm rounded-2xl rounded-tl-sm border-green-400/25 bg-green-400/10 px-4 py-3 text-green-50',
  accent:
    'rounded-2xl border-[var(--color-brand-accent)]/35 bg-[var(--color-brand-accent)]/[0.06] px-5 py-4 shadow-[0_14px_44px_-20px_rgba(0,229,255,0.6)]',
  success: 'rounded-2xl border-green-400/30 bg-green-400/[0.06] px-5 py-4',
  alert: 'rounded-2xl border-[var(--marker)]/30 bg-[var(--marker)]/[0.06] px-5 py-4',
}

const LINE_TONE: Record<NonNullable<StoryLine['tone']>, string> = {
  ok: 'text-green-300',
  info: 'text-[var(--color-brand-accent-readable)]',
  muted: 'text-[var(--surface-muted)]',
}

function Typing() {
  return (
    <span
      aria-hidden="true"
      className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-3 py-2"
    >
      <span className="typing-dot size-1.5 rounded-full bg-[var(--surface-muted)]" />
      <span className="typing-dot size-1.5 rounded-full bg-[var(--surface-muted)]" />
      <span className="typing-dot size-1.5 rounded-full bg-[var(--surface-muted)]" />
    </span>
  )
}

function StoryBody({ item }: { item: StoryItem }) {
  const tone = item.tone ?? 'default'

  if (item.author) {
    return (
      <div className="flex gap-3">
        <span
          aria-hidden="true"
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-lg font-code text-[11px] font-semibold',
            item.author.highlight
              ? 'bg-[var(--marker)] text-[#1a1405]'
              : 'border border-white/10 bg-white/[0.06] text-[var(--text-strong)]',
          )}
        >
          {item.author.initials}
        </span>
        <div className="min-w-0 text-sm leading-relaxed">
          <p className="font-semibold text-[var(--text-strong)]">{item.author.name}</p>
          <div className="mt-0.5 text-[var(--surface-fg)]">{item.body}</div>
          {item.typing && <Typing />}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('border text-sm leading-relaxed', CARD[tone])}>
      {item.title && <p className="font-semibold text-[var(--text-strong)]">{item.title}</p>}
      <div
        className={cn(
          item.title && 'mt-1',
          tone === 'message' ? '' : 'text-[var(--surface-muted)]',
        )}
      >
        {item.body}
      </div>
      {item.lines && (
        <div className="mt-3 rounded-xl border border-white/[0.06] bg-black/40 px-3.5 py-3 font-code text-[12px] leading-6">
          {item.lines.map((line) => (
            <p key={line.text} className={LINE_TONE[line.tone ?? 'muted']}>
              {line.text}
            </p>
          ))}
        </div>
      )}
      {item.typing && <Typing />}
    </div>
  )
}

export function StoryTimeline({ label, items }: { label: string; items: StoryItem[] }) {
  return (
    <ol aria-label={label} className="relative">
      {items.map((item, index) => {
        const tone = item.tone ?? 'default'
        return (
          <li
            key={`${item.label}-${index}`}
            className="group relative grid grid-cols-[76px_1fr] gap-x-5 pb-10 last:pb-0 sm:grid-cols-[104px_1fr]"
          >
            <span className="pt-0.5 text-right font-code text-[10px] uppercase leading-5 tracking-[0.06em] text-[var(--surface-muted)] sm:text-[11px] sm:tracking-[0.14em]">
              {item.label}
            </span>
            <span
              aria-hidden="true"
              className="absolute bottom-0 left-[86px] top-6 w-px bg-gradient-to-b from-[var(--surface-border-strong)] to-[var(--surface-border)] group-last:hidden sm:left-[114px]"
            />
            <span
              aria-hidden="true"
              className={cn(
                'absolute left-[82px] top-1.5 size-[9px] rounded-full sm:left-[110px]',
                DOT[tone],
              )}
            />
            <Reveal delay={60} className="ml-4 min-w-0">
              {item.channel && (
                <p className="mb-2 font-code text-[11px] text-[var(--surface-muted)]">
                  {item.channel}
                </p>
              )}
              <StoryBody item={item} />
              {item.note && <div className="mt-2">{item.note}</div>}
            </Reveal>
          </li>
        )
      })}
    </ol>
  )
}
